import { useCallback, useEffect, useState } from "react";
import {
  AlertCircle,
  CheckCircle2,
  Clock3,
  RefreshCw,
  ShoppingBag,
} from "lucide-react";
import { useNavigate, useSearchParams } from "react-router-dom";

import { useCustomerAuth } from "../context/CustomerAuthContext";
import { apiRequest } from "../services/api";

const CART_KEY = "cart";
const PENDING_ONLINE_ORDER_KEY = "pendingOnlineOrderId";

const formatCurrency = (value) =>
  `₹${Number(value || 0).toLocaleString("en-IN", {
    maximumFractionDigits: 2,
  })}`;

export default function HdfcPaymentReturnPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { accessToken, isAuthenticated, isLoading } = useCustomerAuth();

  const orderId = searchParams.get("orderId");

  const [payment, setPayment] = useState(null);
  const [error, setError] = useState("");
  const [isChecking, setIsChecking] = useState(true);

  const loadStatus = useCallback(async () => {
    if (!accessToken || !orderId) return;

    setIsChecking(true);
    setError("");

    try {
      const response = await apiRequest(
        `/customer-orders/${orderId}/payments/hdfc/status`,
        { accessToken },
      );

      setPayment(response);

      if (response?.paymentStatus === "PAID") {
        localStorage.removeItem(CART_KEY);
        localStorage.removeItem(PENDING_ONLINE_ORDER_KEY);
        window.dispatchEvent(new Event("cartUpdated"));
      }

      if (response?.paymentStatus === "FAILED") {
        localStorage.removeItem(PENDING_ONLINE_ORDER_KEY);
      }
    } catch (requestError) {
      setError(
        requestError?.message ||
          "Unable to verify your payment. Please try again.",
      );
    } finally {
      setIsChecking(false);
    }
  }, [accessToken, orderId]);

  useEffect(() => {
    if (!isLoading && !isAuthenticated) {
      navigate("/signin", {
        replace: true,
        state: {
          from: `/payment/return?orderId=${orderId || ""}`,
        },
      });
    }
  }, [isAuthenticated, isLoading, navigate, orderId]);

  useEffect(() => {
    loadStatus();
  }, [loadStatus]);

  useEffect(() => {
    if (payment?.paymentStatus !== "PENDING") return undefined;

    const timer = window.setInterval(loadStatus, 4000);

    return () => window.clearInterval(timer);
  }, [loadStatus, payment?.paymentStatus]);

  if (isLoading || !isAuthenticated) {
    return (
      <div className="flex min-h-[70vh] items-center justify-center bg-[#faf9f7]">
        <div className="h-10 w-10 animate-spin rounded-full border-4 border-primary-100 border-t-primary-800" />
      </div>
    );
  }

  if (!orderId) {
    return (
      <main className="flex min-h-[70vh] items-center justify-center bg-[#faf9f7] px-4">
        <section className="w-full max-w-lg border border-[#ded8d4] bg-white p-8 text-center">
          <AlertCircle className="mx-auto text-red-700" size={34} />
          <h1 className="mt-5 text-2xl font-semibold text-slate-950">
            Payment reference missing
          </h1>
          <button
            type="button"
            onClick={() => navigate("/userprofile")}
            className="mt-6 bg-primary-800 px-6 py-3 text-sm font-bold text-white"
          >
            View my orders
          </button>
        </section>
      </main>
    );
  }

  const isPaid = payment?.paymentStatus === "PAID";
  const isFailed = payment?.paymentStatus === "FAILED";
  const isPending = !payment || payment.paymentStatus === "PENDING";

  return (
    <main className="flex min-h-[70vh] items-center justify-center bg-[#faf9f7] px-4 py-12">
      <section className="w-full max-w-xl border border-[#ded8d4] bg-white p-7 text-center sm:p-12">
        {isPaid ? (
          <CheckCircle2 className="mx-auto text-emerald-700" size={54} />
        ) : isFailed ? (
          <AlertCircle className="mx-auto text-red-700" size={54} />
        ) : (
          <Clock3 className="mx-auto animate-pulse text-primary-800" size={54} />
        )}

        <p className="mt-7 text-xs font-bold uppercase tracking-[0.2em] text-primary-800">
          HDFC SmartGateway
        </p>

        <h1 className="mt-3 text-3xl font-semibold tracking-tight text-slate-950">
          {isPaid
            ? "Payment successful"
            : isFailed
              ? "Payment was not completed"
              : "Verifying your payment"}
        </h1>

        <p className="mx-auto mt-4 max-w-md text-sm leading-6 text-slate-500">
          {isPaid
            ? `Your order ${payment?.orderNumber} is confirmed.`
            : isFailed
              ? payment?.paymentAttempt?.failureMessage ||
                "No amount was captured. You can return to checkout and try again."
              : "Please wait a moment. We are securely confirming the payment with HDFC."}
        </p>

        {payment ? (
          <div className="mt-8 grid gap-px border border-slate-200 bg-slate-200 sm:grid-cols-2">
            <div className="bg-white px-5 py-5 text-left">
              <p className="text-[10px] font-bold uppercase tracking-[0.15em] text-slate-500">
                Order reference
              </p>
              <p className="mt-2 font-semibold text-slate-950">
                {payment.orderNumber}
              </p>
            </div>

            <div className="bg-white px-5 py-5 text-left">
              <p className="text-[10px] font-bold uppercase tracking-[0.15em] text-slate-500">
                Amount
              </p>
              <p className="mt-2 font-semibold text-slate-950">
                {formatCurrency(payment.totalAmount)}
              </p>
            </div>
          </div>
        ) : null}

        {error ? (
          <p className="mt-6 text-sm text-red-700">{error}</p>
        ) : null}

        <div className="mt-8 grid gap-3 sm:grid-cols-2">
          {isPending ? (
            <button
              type="button"
              onClick={loadStatus}
              disabled={isChecking}
              className="inline-flex items-center justify-center gap-2 border border-slate-900 py-4 text-sm font-bold text-slate-950 disabled:opacity-50"
            >
              <RefreshCw
                size={16}
                className={isChecking ? "animate-spin" : ""}
              />
              Check again
            </button>
          ) : (
            <button
              type="button"
              onClick={() => navigate("/userprofile")}
              className="border border-slate-900 py-4 text-sm font-bold text-slate-950"
            >
              View my orders
            </button>
          )}

          <button
            type="button"
            onClick={() => navigate(isFailed ? "/checkout" : "/search/all")}
            className="inline-flex items-center justify-center gap-2 bg-primary-800 py-4 text-sm font-bold text-white"
          >
            <ShoppingBag size={16} />
            {isFailed ? "Return to checkout" : "Continue shopping"}
          </button>
        </div>
      </section>
    </main>
  );
}